import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

// Diverging Bar Chart Component - Shows performance relative to industry average
export const DivergingBarChart: React.FC<ChartProps> = ({ metrics, chartId = 'diverging' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (absoluteDiff: number) => {
    const perfLevel = getPerformanceLevel(absoluteDiff);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!chartRef.current || !containerRef.current || metrics.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const margin = { top: 40, right: 80, bottom: 60, left: 200 };
    const width = containerRect.width - margin.left - margin.right;
    const height = Math.max(400, metrics.length * 35);

    svg.attr('width', containerRect.width).attr('height', height + margin.top + margin.bottom);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.diverging-tooltip-${chartId}`)
      .data([null])
      .join('div')
      .attr('class', `diverging-tooltip diverging-tooltip-${chartId}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Filter metrics with industry data and calculate absolute differences
    const validMetrics = metrics.filter(d => d.industryMean && d.industryMean > 0)
      .map(d => ({
        ...d,
        absoluteDiff: calculateAbsoluteDifference(d.value, d.industryMean || 0)
      }))
      .sort((a, b) => b.absoluteDiff - a.absoluteDiff); // Sort by performance (best first)

    if (validMetrics.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#6b7280')
        .text('No industry benchmark data available');
      return;
    }

    // Calculate scales - use absolute differences
    const maxAbsDiff = d3.max(validMetrics, d => Math.abs(d.absoluteDiff)) || 5;
    const scale = Math.max(5, maxAbsDiff * 1.1); // Minimum 5 percentage points scale

    const xScale = d3.scaleLinear()
      .domain([-scale, scale])
      .range([0, width]);

    const yScale = d3.scaleBand()
      .domain(validMetrics.map(d => d.categoryName))
      .range([0, height])
      .padding(0.2);

    // Add center line (industry average = 0%)
    g.append('line')
      .attr('x1', xScale(0))
      .attr('x2', xScale(0))
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', '5,5');

    // Add center line label
    g.append('text')
      .attr('x', xScale(0))
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#94a3b8')
      .text('Industry Average');

    // Add performance zone backgrounds - using absolute difference thresholds
    const zones = [
      { start: -scale, end: -5, color: '#fecaca', label: 'Poor', opacity: 0.15 },
      { start: -5, end: -2, color: '#fed7aa', label: 'Below Avg', opacity: 0.15 },
      { start: -2, end: 2, color: '#f3f4f6', label: 'Average', opacity: 0.1 },
      { start: 2, end: 5, color: '#ecfccb', label: 'Above Avg', opacity: 0.15 },
      { start: 5, end: scale, color: '#dcfce7', label: 'Excellent', opacity: 0.15 }
    ];

    zones.forEach(zone => {
      g.append('rect')
        .attr('x', xScale(zone.start))
        .attr('y', 0)
        .attr('width', xScale(zone.end) - xScale(zone.start))
        .attr('height', height)
        .attr('fill', zone.color)
        .attr('opacity', zone.opacity);
    });

    // Draw bars
    validMetrics.forEach(d => {
      const colors = getPerformanceColors(d.absoluteDiff);
      const barWidth = Math.abs(xScale(d.absoluteDiff) - xScale(0));
      const barStart = d.absoluteDiff >= 0 ? xScale(0) : xScale(d.absoluteDiff);

      g.append('rect')
        .attr('x', barStart)
        .attr('y', yScale(d.categoryName) || 0)
        .attr('width', barWidth)
        .attr('height', yScale.bandwidth())
        .attr('fill', colors.color)
        .attr('rx', 4)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const performanceText = colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Difference:</span>
                <span style="font-weight: 600; color: ${d.absoluteDiff >= 0 ? colors.color : '#dc2626'}; font-size: 12px;">
                  ${d.absoluteDiff >= 0 ? '+' : ''}${d.absoluteDiff.toFixed(1)}%
                </span>
              </div>
            </div>
          `)
          .style('visibility', 'visible');

          d3.select(this).attr('opacity', 0.8);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('opacity', 1);
        });

      // Add metric labels
      g.append('text')
        .attr('x', -10)
        .attr('y', (yScale(d.categoryName) || 0) + yScale.bandwidth() / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .attr('fill', '#374151')
        .text(d.categoryName.length > 25 ? d.categoryName.substring(0, 25) + '...' : d.categoryName);

      // Add absolute difference labels on bars
      const labelX = d.absoluteDiff >= 0 ?
        xScale(d.absoluteDiff) + 5 :
        xScale(d.absoluteDiff) - 5;

      g.append('text')
        .attr('x', labelX)
        .attr('y', (yScale(d.categoryName) || 0) + yScale.bandwidth() / 2)
        .attr('text-anchor', d.absoluteDiff >= 0 ? 'start' : 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', colors.color)
        .text(`${d.absoluteDiff >= 0 ? '+' : ''}${d.absoluteDiff.toFixed(1)}%`);
    });

    // Add x-axis
    const xAxis = d3.axisBottom(xScale)
      .tickFormat(d => `${d}%`)
      .ticks(6);

    g.append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '11px')
      .style('fill', '#6b7280');

    // Style the axis
    g.selectAll('.domain')
      .style('stroke', '#d1d5db')
      .style('stroke-width', 1);

    g.selectAll('.tick line')
      .style('stroke', '#d1d5db')
      .style('stroke-width', 1);

    // Add axis label
    g.append('text')
      .attr('x', width / 2)
      .attr('y', height + 45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('fill', '#6b7280')
      .text('Absolute Difference vs Industry Average (percentage points)');

    // Cleanup function
    return () => {
      d3.select('body').select(`.diverging-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};